import type { GeoZoomTier } from '@pulsify/shared/dto'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useSupabaseSession } from '~/entities/session/model/use-auth'
import { statsApi } from '~/entities/stats/api/stats'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { storageManager } from '~/shared/lib/storage-manager'
import { MINUTES } from '~/shared/lib/time-constants'

function useAdminStatsEnabled() {
  const session = useSupabaseSession()
  return !!session && storageManager.isAdmin()
}

export function useAdminUserStats() {
  const enabled = useAdminStatsEnabled()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_STATS, 'users'),
    queryFn: () => statsApi.getAdminUserStats(),
    staleTime: 5 * MINUTES,
    enabled,
  })
}

export function useAdminPlanStats() {
  const enabled = useAdminStatsEnabled()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_STATS, 'plans'),
    queryFn: () => statsApi.getAdminPlanStats(),
    staleTime: 5 * MINUTES,
    enabled,
  })
}

export function useAdminLiveRoomGuestStats() {
  const enabled = useAdminStatsEnabled()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_STATS, 'live-room-guests'),
    queryFn: () => statsApi.getAdminLiveRoomGuestStats(),
    staleTime: 5 * MINUTES,
    enabled,
  })
}

export function useAdminAiCosts() {
  const enabled = useAdminStatsEnabled()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_STATS, 'ai-costs'),
    queryFn: () => statsApi.getAdminAiCosts(),
    staleTime: 5 * MINUTES,
    enabled,
  })
}

export function useAdminRecentSignups(params: { days: number; limit: number }) {
  const enabled = useAdminStatsEnabled()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_SIGNUPS, String(params.days), String(params.limit)),
    queryFn: () => statsApi.getAdminRecentSignups(params),
    staleTime: 1 * MINUTES,
    enabled,
  })
}

export function useAdminGeoActivity(params: { tier: GeoZoomTier; days: number }) {
  const enabled = useAdminStatsEnabled()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_GEO, params.tier, String(params.days)),
    queryFn: () => statsApi.getAdminGeoActivity(params),
    staleTime: 1 * MINUTES,
    // Crossing a zoom tier starts a new query. Without the previous answer standing in, the
    // page would fall back to its loading state mid-gesture and tear the map down with it.
    placeholderData: keepPreviousData,
    enabled,
  })
}

export function useAdminGrowthOpportunities(params: { days: number }) {
  const enabled = useAdminStatsEnabled()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_GROWTH, String(params.days)),
    queryFn: () => statsApi.getAdminGrowthOpportunities(params),
    staleTime: 5 * MINUTES,
    placeholderData: keepPreviousData,
    enabled,
  })
}
